/**
 * OrganisationCard Component
 *
 * Displays an organisation with clickable role buttons.
 * Used on the RoleNavigationPage for users with multiple organisations/roles.
 */

import type { OrganisationRole } from '../types/navigation';
import './OrganisationCard.css';

interface OrganisationCardProps {
  /** The organisation to display */
  organisation: OrganisationRole;
  /** Callback when a role button is clicked */
  onSelectRole: (orgSlug: string, role: string) => void;
  /** Whether navigation is in progress (disables buttons) */
  isLoading: boolean;
}

/** Human-readable labels for each role */
const roleLabels: Record<string, string> = {
  partner: 'Partner Dashboard',
  supplier: 'Supplier Portal',
  hirer: 'Hirer Portal',
  admin: 'Admin Console',
};

/** Icons for each role */
const roleIcons: Record<string, string> = {
  partner: '🤝',
  supplier: '📦',
  hirer: '🏗️',
  admin: '⚙️',
};

/**
 * OrganisationCard Component
 *
 * Renders a card showing organisation name and role buttons.
 * Each role button navigates to the corresponding downstream app.
 */
export function OrganisationCard({
  organisation,
  onSelectRole,
  isLoading,
}: OrganisationCardProps) {
  return (
    <div className="org-card">
      <h3 className="org-card-title">{organisation.name}</h3>
      <p className="org-card-slug">{organisation.slug}</p>

      <div className="org-card-roles">
        {organisation.roles.map((role) => (
          <button
            key={role}
            className={`role-button role-${role}`}
            onClick={() => onSelectRole(organisation.slug, role)}
            disabled={isLoading}
            aria-label={`Open ${roleLabels[role] || role} for ${organisation.name}`}
          >
            <span className="role-icon" aria-hidden="true">
              {roleIcons[role] || '📋'}
            </span>
            <span className="role-label">{roleLabels[role] || role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
